/**
 * Project Control State Ravioli Container
 * @implements FS-002.6 - Project Control States
 * @implements FS-002.6.1 - Control State Definitions
 * @implements FS-002.8.2 - JSON Patch Protocol
 * @implements TS-074 - Control State Ravioli Container
 *
 * This container manages the project control state using the SAM (State-Action-Model) pattern.
 * Control states are computed from two validation booleans:
 * - NORMAL: pmValidated && poValidated
 * - DRAFT: !pmValidated
 * - PENDING: pmValidated && !poValidated
 *
 * Actions are gated by control state predicates using the isAllowed pattern.
 */

import { createContainer } from '@warfog/ravioli';

/**
 * Control state type as returned from backend
 * @implements FS-002.6.1 - Control State Definitions
 */
export type ControlState = 'NORMAL' | 'DRAFT' | 'PENDING';

/**
 * JSON Patch operation type following RFC 6902
 * @implements FS-002.8.2 - JSON Patch Protocol
 */
export interface JSONPatchOperation {
  /** The operation to perform: add, remove, replace, move, copy, test */
  op: 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';
  /** JSON Pointer path to the target location */
  path: string;
  /** The value for the operation (required for add, replace, test) */
  value?: unknown;
}

/**
 * Project control state data stored in the Ravioli container
 * @implements FS-002.6.1 - Control State Definitions
 */
export interface ProjectControlStateData {
  /** Project ID */
  projectId: string;
  /** PM validation flag - true when PM has validated the current state */
  pmValidated: boolean;
  /** PO validation flag - true when PO has validated the current state */
  poValidated: boolean;
  /** Computed control state from backend (for reference/validation) */
  controlState: ControlState;
  /** Loading state for async operations */
  isLoading: boolean;
  /** Error message if last operation failed */
  error: string | null;
}

/**
 * Callback type for mutation execution
 * Allows the container to trigger GraphQL mutations through the hook
 */
export type MutationCallback = (projectId: string) => Promise<{
  pmValidated: boolean;
  poValidated: boolean;
  controlState: ControlState;
}>;

/**
 * Mutation callbacks configuration for the container
 * These are set by the hook to connect container actions to GraphQL mutations
 */
export interface MutationCallbacks {
  enterDraftMode: MutationCallback;
  validateAsPm: MutationCallback;
  validateAsPo: MutationCallback;
  rejectChanges: MutationCallback;
  discardDraft: MutationCallback;
}

/**
 * Default initial state for a new project control state container
 */
export const defaultInitialState: ProjectControlStateData = {
  projectId: '',
  pmValidated: true,
  poValidated: true,
  controlState: 'NORMAL',
  isLoading: false,
  error: null,
};

/**
 * Create the Project Control State container factory
 * @implements TS-074 - Control State Ravioli Container
 *
 * The container uses:
 * - Atomic acceptors for state mutations
 * - Control state predicates for computed states
 * - Gated actions with isAllowed pattern
 */
export const ProjectControlStateContainer =
  createContainer<ProjectControlStateData>()
    // ============================================
    // ACCEPTORS (Atomic Mutations)
    // ============================================

    /**
     * Set PM validation flag
     * @implements FS-002.6.1 - Control State Definitions
     */
    .addAcceptor('setPmValidation', {
      mutator: (data, { value }: { value: boolean }) => {
        data.pmValidated = value;
        // Update computed control state
        if (!value) {
          data.controlState = 'DRAFT';
        } else if (!data.poValidated) {
          data.controlState = 'PENDING';
        } else {
          data.controlState = 'NORMAL';
        }
      },
    })

    /**
     * Set PO validation flag
     * @implements FS-002.6.1 - Control State Definitions
     */
    .addAcceptor('setPoValidation', {
      mutator: (data, { value }: { value: boolean }) => {
        data.poValidated = value;
        // Update computed control state
        if (!data.pmValidated) {
          data.controlState = 'DRAFT';
        } else if (!value) {
          data.controlState = 'PENDING';
        } else {
          data.controlState = 'NORMAL';
        }
      },
    })

    /**
     * Set both validation flags at once (for optimistic updates)
     */
    .addAcceptor('setValidationFlags', {
      mutator: (
        data,
        {
          pmValidated,
          poValidated,
        }: { pmValidated: boolean; poValidated: boolean },
      ) => {
        data.pmValidated = pmValidated;
        data.poValidated = poValidated;
        // Update computed control state
        if (!pmValidated) {
          data.controlState = 'DRAFT';
        } else if (!poValidated) {
          data.controlState = 'PENDING';
        } else {
          data.controlState = 'NORMAL';
        }
      },
    })

    /**
     * Set loading state
     */
    .addAcceptor('setLoading', {
      mutator: (data, { isLoading }: { isLoading: boolean }) => {
        data.isLoading = isLoading;
      },
    })

    /**
     * Set error state
     */
    .addAcceptor('setError', {
      mutator: (data, { error }: { error: string | null }) => {
        data.error = error;
      },
    })

    /**
     * Apply JSON Patch operations to update state from server
     * @implements FS-002.8.2 - JSON Patch Protocol
     */
    .addAcceptor('applyPatch', {
      mutator: (data, { patch }: { patch: JSONPatchOperation[] }) => {
        for (const operation of patch) {
          // Parse path (e.g., "/pmValidated" -> "pmValidated")
          const pathSegments = operation.path.split('/').filter(Boolean);
          if (pathSegments.length !== 1) continue;

          const field = pathSegments[0];

          switch (operation.op) {
            case 'replace':
            case 'add':
              if (
                field === 'pmValidated' &&
                typeof operation.value === 'boolean'
              ) {
                data.pmValidated = operation.value;
              } else if (
                field === 'poValidated' &&
                typeof operation.value === 'boolean'
              ) {
                data.poValidated = operation.value;
              } else if (
                field === 'controlState' &&
                typeof operation.value === 'string'
              ) {
                data.controlState =
                  operation.value.toUpperCase() as ControlState;
              }
              break;
            // For control state, we only care about replace operations
            // remove, move, copy, test are not applicable
          }
        }
      },
    })

    /**
     * Sync state from server response (full update)
     */
    .addAcceptor('syncFromServer', {
      mutator: (
        data,
        {
          pmValidated,
          poValidated,
          controlState,
        }: { pmValidated: boolean; poValidated: boolean; controlState: string },
      ) => {
        data.pmValidated = pmValidated;
        data.poValidated = poValidated;
        data.controlState = controlState.toUpperCase() as ControlState;
        data.error = null;
      },
    })

    // ============================================
    // CONTROL STATE PREDICATES
    // ============================================

    /**
     * NORMAL state: Both PM and PO have validated
     * @implements FS-002.6.1 - Control State Definitions
     */
    .addControlStatePredicate(
      'isNormal',
      ({ data }) => data.pmValidated && data.poValidated,
    )

    /**
     * DRAFT state: PM has not validated
     * @implements FS-002.6.1 - Control State Definitions
     */
    .addControlStatePredicate('isDraft', ({ data }) => !data.pmValidated)

    /**
     * PENDING state: PM has validated but PO has not
     * @implements FS-002.6.1 - Control State Definitions
     */
    .addControlStatePredicate(
      'isPending',
      ({ data }) => data.pmValidated && !data.poValidated,
    )

    /**
     * Loading state for UI feedback
     */
    .addControlStatePredicate('isLoading', ({ data }) => data.isLoading)

    /**
     * Has error state for UI feedback
     */
    .addControlStatePredicate('hasError', ({ data }) => data.error !== null)

    // ============================================
    // ACTIONS (Gated by Control State)
    // ============================================

    .addActions({
      /**
       * Enter draft mode - allowed only when in NORMAL state
       * Transitions: NORMAL -> DRAFT
       * @implements FS-002.6.1 - Control State Definitions (Normal -> Draft)
       */
      enterDraftMode: {
        isAllowed: ({ controlStates }) => controlStates.includes('isNormal'),
        action: () => [
          { type: 'setLoading', payload: { isLoading: true } },
          { type: 'setError', payload: { error: null } },
          {
            type: 'setValidationFlags',
            payload: { pmValidated: false, poValidated: false },
          },
        ],
      },

      /**
       * Validate as PM - allowed only when in DRAFT state
       * Transitions: DRAFT -> PENDING
       * @implements FS-002.6.1 - Control State Definitions (Draft -> Pending)
       */
      validateAsPm: {
        isAllowed: ({ controlStates }) => controlStates.includes('isDraft'),
        action: () => [
          { type: 'setLoading', payload: { isLoading: true } },
          { type: 'setError', payload: { error: null } },
          { type: 'setPmValidation', payload: { value: true } },
        ],
      },

      /**
       * Validate as PO - allowed only when in PENDING state
       * Transitions: PENDING -> NORMAL
       * @implements FS-002.6.1 - Control State Definitions (Pending -> Normal)
       */
      validateAsPo: {
        isAllowed: ({ controlStates }) => controlStates.includes('isPending'),
        action: () => [
          { type: 'setLoading', payload: { isLoading: true } },
          { type: 'setError', payload: { error: null } },
          { type: 'setPoValidation', payload: { value: true } },
        ],
      },

      /**
       * Reject changes - allowed only when in PENDING state
       * Transitions: PENDING -> DRAFT
       * @implements FS-002.6.1 - Control State Definitions (Pending -> Draft)
       */
      rejectChanges: {
        isAllowed: ({ controlStates }) => controlStates.includes('isPending'),
        action: () => [
          { type: 'setLoading', payload: { isLoading: true } },
          { type: 'setError', payload: { error: null } },
          { type: 'setPmValidation', payload: { value: false } },
        ],
      },

      /**
       * Discard draft - allowed only when in DRAFT state
       * Transitions: DRAFT -> NORMAL
       * @implements FS-002.6.4 - Discard Draft Changes
       */
      discardDraft: {
        isAllowed: ({ controlStates }) => controlStates.includes('isDraft'),
        action: () => [
          { type: 'setLoading', payload: { isLoading: true } },
          { type: 'setError', payload: { error: null } },
          {
            type: 'setValidationFlags',
            payload: { pmValidated: true, poValidated: true },
          },
        ],
      },

      /**
       * Complete mutation - called after GraphQL mutation succeeds
       */
      completeMutation: () => [
        { type: 'setLoading', payload: { isLoading: false } },
        { type: 'setError', payload: { error: null } },
      ],

      /**
       * Handle mutation error - called when GraphQL mutation fails
       */
      handleError: (error: string) => [
        { type: 'setLoading', payload: { isLoading: false } },
        { type: 'setError', payload: { error } },
      ],

      /**
       * Sync state from server - called after receiving server response
       */
      syncFromServer: 'syncFromServer',

      /**
       * Apply JSON Patch from subscription
       * @implements FS-002.8.2 - JSON Patch Protocol
       */
      applyPatch: 'applyPatch',
    });

/**
 * Type for a created container instance
 */
export type ProjectControlStateInstance = ReturnType<
  typeof ProjectControlStateContainer.create
>;

/**
 * Factory function to create a new container instance with initial data
 * @param initialData - Initial project control state data
 * @returns A new container instance
 */
export const createProjectControlStateContainer = (
  initialData: Partial<ProjectControlStateData> = {},
): ProjectControlStateInstance => {
  return ProjectControlStateContainer.create({
    ...defaultInitialState,
    ...initialData,
  });
};
